import { 
  doc, 
  onSnapshot, 
  updateDoc, 
  arrayUnion, 
  arrayRemove, 
  serverTimestamp,
  getDoc,
  setDoc 
} from 'firebase/firestore';
import { db } from './firebase';
import { COLLECTIONS } from '../utils/constants';
import { withErrorHandling, ERROR_TYPES, createError, logError, networkManager } from '../utils/errorHandling';

// Reference to the global canvas document
const getCanvasRef = () => doc(db, COLLECTIONS.CANVAS, COLLECTIONS.GLOBAL_CANVAS_ID);

/**
 * Subscribe to real-time updates for canvas shapes
 * @param {Function} callback - Function to call with shapes array on each update
 * @returns {Function} unsubscribe function
 */
export function subscribeToShapes(callback) {
  try {
    const canvasRef = getCanvasRef();
    
    const unsubscribe = onSnapshot(canvasRef, 
      async (doc) => {
        try {
          if (doc.exists()) {
            const data = doc.data();
            const shapes = data.shapes || [];
            callback(shapes);
            console.log('📊 Shapes updated from Firestore:', shapes.length, 'shapes');
          } else {
            // Document doesn't exist, create it with empty shapes array
            console.log('📄 Canvas document does not exist, creating...');
            await withErrorHandling(
              () => setDoc(canvasRef, {
                canvasId: COLLECTIONS.GLOBAL_CANVAS_ID,
                shapes: [],
                lastUpdated: serverTimestamp()
              }),
              'Initialize Canvas Document',
              { errorType: ERROR_TYPES.FIRESTORE }
            );
            callback([]);
          }
        } catch (error) {
          logError(createError(error, ERROR_TYPES.FIRESTORE, 'medium'), 'Shapes subscription callback');
          callback([]);
        }
      }, 
      (error) => {
        logError(createError(error, ERROR_TYPES.FIRESTORE, 'high'), 'Shapes subscription listener');
        // Call callback with empty array on error to prevent app crash
        callback([]);
      }
    );

    return unsubscribe;
  } catch (error) {
    logError(createError(error, ERROR_TYPES.FIRESTORE, 'high'), 'Setting up shapes subscription');
    // Return a no-op function if setup fails
    return () => {};
  }
}

/**
 * Create a new shape in Firestore
 * @param {Object} shapeData - Shape data (x, y, width, height, etc.)
 * @param {string} userId - ID of the user creating the shape
 * @returns {Promise<Object>} Result with shape ID and success status
 */
export async function createShape(shapeData, userId) {
  return await withErrorHandling(
    async () => {
      // Validate inputs
      if (!shapeData || !userId) {
        throw createError(
          'Shape data and user ID are required',
          ERROR_TYPES.VALIDATION,
          'medium'
        );
      }

      const canvasRef = getCanvasRef();
      
      // Create shape object with metadata
      // Note: Using Date.now() instead of serverTimestamp() because 
      // serverTimestamp() cannot be used inside arrayUnion()
      const now = Date.now();
      const newShape = {
        ...shapeData,
        createdBy: userId,
        createdAt: now,
        lastModifiedBy: userId,
        lastModifiedAt: now,
        isLocked: false,
        lockedBy: null
      };

      // Get current document to check if it exists
      const docSnap = await getDoc(canvasRef);
      
      if (!docSnap.exists()) {
        // Create document with the new shape
        await setDoc(canvasRef, {
          canvasId: COLLECTIONS.GLOBAL_CANVAS_ID,
          shapes: [newShape],
          lastUpdated: serverTimestamp()
        });
      } else {
        // Update existing document by adding shape to array
        await updateDoc(canvasRef, {
          shapes: arrayUnion(newShape),
          lastUpdated: serverTimestamp()
        });
      }

      console.log('✅ Shape created in Firestore:', newShape.id);
      return {
        success: true,
        shapeId: newShape.id,
        message: 'Shape created successfully'
      };
    },
    'Create Shape',
    { 
      errorType: ERROR_TYPES.FIRESTORE,
      enableRetry: true,
      enableOfflineQueue: true
    }
  );
}

/**
 * Update an existing shape in Firestore
 * @param {string} shapeId - ID of the shape to update
 * @param {Object} updates - Updates to apply to the shape
 * @param {string} userId - ID of the user making the update
 * @returns {Promise<Object>} Result with success status
 */
export async function updateShape(shapeId, updates, userId) {
  return await withErrorHandling(
    async () => {
      // Validate inputs
      if (!shapeId || !updates || !userId) {
        throw createError(
          'Shape ID, updates, and user ID are required',
          ERROR_TYPES.VALIDATION,
          'medium'
        );
      }

      const canvasRef = getCanvasRef();
      const docSnap = await getDoc(canvasRef);
      
      if (!docSnap.exists()) {
        throw createError(
          'Canvas document does not exist',
          ERROR_TYPES.FIRESTORE,
          'high'
        );
      }

      const data = docSnap.data();
      const shapes = data.shapes || [];
      
      // Find the shape to update
      const shapeIndex = shapes.findIndex(shape => shape.id === shapeId);
      if (shapeIndex === -1) {
        throw createError(
          `Shape with id ${shapeId} not found`,
          ERROR_TYPES.FIRESTORE,
          'medium'
        );
      }

      // Create updated shape with new metadata
      const updatedShape = {
        ...shapes[shapeIndex],
        ...updates,
        lastModifiedBy: userId,
        lastModifiedAt: Date.now()
      };

      // Create new shapes array with updated shape
      const updatedShapes = [...shapes];
      updatedShapes[shapeIndex] = updatedShape;

      // Update Firestore document
      await updateDoc(canvasRef, {
        shapes: updatedShapes,
        lastUpdated: serverTimestamp()
      });

      console.log('✅ Shape updated in Firestore:', shapeId);
      return {
        success: true,
        message: 'Shape updated successfully'
      };
    },
    'Update Shape',
    { 
      errorType: ERROR_TYPES.FIRESTORE,
      enableRetry: true,
      enableOfflineQueue: true
    }
  );
}

/**
 * Delete a shape from Firestore
 * @param {string} shapeId - ID of the shape to delete
 * @returns {Promise<Object>} Result with success status
 */
export async function deleteShape(shapeId) {
  return await withErrorHandling(
    async () => {
      // Validate input
      if (!shapeId) {
        throw createError(
          'Shape ID is required',
          ERROR_TYPES.VALIDATION,
          'medium'
        );
      }

      const canvasRef = getCanvasRef();
      const docSnap = await getDoc(canvasRef);
      
      if (!docSnap.exists()) {
        throw createError(
          'Canvas document does not exist',
          ERROR_TYPES.FIRESTORE,
          'high'
        );
      }

      const data = docSnap.data();
      const shapes = data.shapes || [];
      
      // Find the shape to delete
      const shapeToDelete = shapes.find(shape => shape.id === shapeId);
      if (!shapeToDelete) {
        throw createError(
          `Shape with id ${shapeId} not found`,
          ERROR_TYPES.FIRESTORE,
          'medium'
        );
      }

      // Remove shape from array using arrayRemove
      await updateDoc(canvasRef, {
        shapes: arrayRemove(shapeToDelete),
        lastUpdated: serverTimestamp()
      });

      console.log('✅ Shape deleted from Firestore:', shapeId);
      return {
        success: true,
        message: 'Shape deleted successfully'
      };
    },
    'Delete Shape',
    { 
      errorType: ERROR_TYPES.FIRESTORE,
      enableRetry: true,
      enableOfflineQueue: true
    }
  );
}

/**
 * Lock a shape for editing by a specific user
 * @param {string} shapeId - ID of the shape to lock
 * @param {string} userId - ID of the user locking the shape
 * @returns {Promise<Object>} Result with success status
 */
export async function lockShape(shapeId, userId) {
  return await withErrorHandling(
    async () => {
      // Validate inputs
      if (!shapeId || !userId) {
        throw createError(
          'Shape ID and user ID are required',
          ERROR_TYPES.VALIDATION,
          'medium'
        );
      }

      const canvasRef = getCanvasRef();
      const docSnap = await getDoc(canvasRef);
      
      if (!docSnap.exists()) {
        throw createError(
          'Canvas document does not exist',
          ERROR_TYPES.FIRESTORE,
          'high'
        );
      }

      const data = docSnap.data();
      const shapes = data.shapes || [];
      
      // Find the shape to lock
      const shapeIndex = shapes.findIndex(shape => shape.id === shapeId);
      if (shapeIndex === -1) {
        throw createError(
          `Shape with id ${shapeId} not found`,
          ERROR_TYPES.FIRESTORE,
          'medium'
        );
      }

      // Check if shape is already locked by another user
      const shape = shapes[shapeIndex];
      if (shape.isLocked && shape.lockedBy !== userId) {
        throw createError(
          `Shape is already locked by another user`,
          ERROR_TYPES.PERMISSION,
          'medium',
          { lockedBy: shape.lockedBy }
        );
      }

      // Update shape with lock info
      const updatedShape = {
        ...shape,
        isLocked: true,
        lockedBy: userId,
        lastModifiedBy: userId,
        lastModifiedAt: Date.now()
      };

      // Create new shapes array with locked shape
      const updatedShapes = [...shapes];
      updatedShapes[shapeIndex] = updatedShape;

      // Update Firestore document
      await updateDoc(canvasRef, {
        shapes: updatedShapes,
        lastUpdated: serverTimestamp()
      });

      console.log('🔒 Shape locked in Firestore:', shapeId, 'by user:', userId);
      return {
        success: true,
        message: 'Shape locked successfully'
      };
    },
    'Lock Shape',
    { 
      errorType: ERROR_TYPES.FIRESTORE,
      enableRetry: true,
      enableOfflineQueue: true
    }
  );
}

/**
 * Unlock a shape to allow editing by other users
 * @param {string} shapeId - ID of the shape to unlock
 * @returns {Promise<Object>} Result with success status
 */
export async function unlockShape(shapeId) {
  return await withErrorHandling(
    async () => {
      // Validate input
      if (!shapeId) {
        throw createError(
          'Shape ID is required',
          ERROR_TYPES.VALIDATION,
          'medium'
        );
      }

      const canvasRef = getCanvasRef();
      const docSnap = await getDoc(canvasRef);
      
      if (!docSnap.exists()) {
        throw createError(
          'Canvas document does not exist',
          ERROR_TYPES.FIRESTORE,
          'high'
        );
      }

      const data = docSnap.data();
      const shapes = data.shapes || [];
      
      // Find the shape to unlock
      const shapeIndex = shapes.findIndex(shape => shape.id === shapeId);
      if (shapeIndex === -1) {
        throw createError(
          `Shape with id ${shapeId} not found`,
          ERROR_TYPES.FIRESTORE,
          'medium'
        );
      }

      // Update shape with unlock info
      const updatedShape = {
        ...shapes[shapeIndex],
        isLocked: false,
        lockedBy: null,
        lastModifiedAt: Date.now()
      };

      // Create new shapes array with unlocked shape
      const updatedShapes = [...shapes];
      updatedShapes[shapeIndex] = updatedShape;

      // Update Firestore document
      await updateDoc(canvasRef, {
        shapes: updatedShapes,
        lastUpdated: serverTimestamp()
      });

      console.log('🔓 Shape unlocked in Firestore:', shapeId);
      return {
        success: true,
        message: 'Shape unlocked successfully'
      };
    },
    'Unlock Shape',
    { 
      errorType: ERROR_TYPES.FIRESTORE,
      enableRetry: true,
      enableOfflineQueue: true
    }
  );
}

/**
 * Get all shapes from Firestore (one-time read)
 * @returns {Promise<Array>} Array of shapes
 */
export async function getShapes() {
  try {
    const canvasRef = getCanvasRef();
    const docSnap = await getDoc(canvasRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      return data.shapes || [];
    } else {
      console.log('Canvas document does not exist');
      return [];
    }
  } catch (error) {
    console.error('Error getting shapes from Firestore:', error);
    throw error;
  }
}

/**
 * Delete all shapes from Firestore
 * @returns {Promise<Object>} Result with success status
 */
export async function deleteAllShapes() {
  return await withErrorHandling(
    async () => {
      const canvasRef = getCanvasRef();
      const docSnap = await getDoc(canvasRef);
      
      if (!docSnap.exists()) {
        throw createError(
          'Canvas document does not exist',
          ERROR_TYPES.FIRESTORE,
          'high'
        );
      }

      const data = docSnap.data();
      const shapesCount = data.shapes?.length || 0;

      // Clear all shapes from the canvas
      await updateDoc(canvasRef, {
        shapes: [],
        lastUpdated: serverTimestamp()
      });

      console.log(`✅ All ${shapesCount} shapes deleted from Firestore`);
      return {
        success: true,
        message: `All ${shapesCount} shapes deleted successfully`,
        deletedCount: shapesCount
      };
    },
    'Delete All Shapes',
    { 
      errorType: ERROR_TYPES.FIRESTORE,
      enableRetry: true,
      enableOfflineQueue: true
    }
  );
}

/**
 * Initialize canvas document if it doesn't exist
 * @returns {Promise<void>}
 */
export async function initializeCanvas() {
  try {
    const canvasRef = getCanvasRef();
    const docSnap = await getDoc(canvasRef);
    
    if (!docSnap.exists()) {
      await setDoc(canvasRef, {
        canvasId: COLLECTIONS.GLOBAL_CANVAS_ID,
        shapes: [],
        lastUpdated: serverTimestamp()
      });
      console.log('Canvas document initialized');
    }
  } catch (error) {
    console.error('Error initializing canvas:', error);
    throw error;
  }
}
